import MyLocationIcon from '@mui/icons-material/MyLocation';
import { IconButton } from '@mui/material';
import React from 'react';
import useMobileMediaQuery from '../../../hooks/useMobileMediaQuery';
import './CurrentLocationButton.scss';

type CurrentLocationButtonProps = {
  onClick: () => void;
  isLoading?: boolean;
  isTracking?: boolean;
  disabled?: boolean;
  error?: string | null;
  variant?: 'default' | 'playground';
};

/**
 * CurrentLocationButton - Button to trigger current location detection
 *
 * Positioned in bottom-right corner of map (like Google Maps).
 * Shows loading state and handles error states gracefully.
 * On mobile, toggles continuous tracking mode (blue when active).
 */
function CurrentLocationButton({
  onClick,
  isLoading = false,
  isTracking = false,
  disabled = false,
  error,
  variant = 'default',
}: CurrentLocationButtonProps) {
  const isMobile = useMobileMediaQuery();

  // Determine icon color based on state
  const getIconColor = () => {
    if (error) {
      return '#d32f2f';
    }
    if (isTracking || isLoading) {
      return '#1976d2'; // Blue when tracking or loading
    }
    return '#666';
  };

  return (
    <div className={`current-location-btn-container ${variant}`}>
      <IconButton
        onClick={onClick}
        disabled={disabled || isLoading}
        size={isMobile ? 'medium' : 'small'}
        sx={{
          backgroundColor: isTracking ? '#e3f2fd' : 'white', // Light blue background when tracking
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          '&:hover': {
            backgroundColor: isTracking ? '#bbdefb' : '#f5f5f5',
          },
          '&:disabled': {
            backgroundColor: '#f5f5f5',
            opacity: 0.6,
          },
          width: 40,
          height: 40,
        }}
      >
        <MyLocationIcon
          sx={{
            color: getIconColor(),
            fontSize: isMobile ? 20 : 18,
            animation: isLoading ? 'spin 1s linear infinite' : 'none',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
      </IconButton>
    </div>
  );
}

CurrentLocationButton.displayName = 'CurrentLocationButton';
export default CurrentLocationButton;
